import type { TriggerType } from '@mytaverse-shared/interfaces/assistantScript';

export const QUESTION_KEYS = [
  'trigger',
  'policy',
  'interrupt',
  'dialogue',
  'languages',
] as const;

export type QuestionKey = (typeof QUESTION_KEYS)[number];

interface QuestionMetaEntry {
  eyebrowKey: string;
  titleKey: string;
  helperKey: string;
}

// i18n keys resolved via t() at render time (see QuestionShell).
export const QUESTION_META: Record<QuestionKey, QuestionMetaEntry> = {
  trigger: {
    eyebrowKey: 'assistant.scriptDialog.trigger',
    titleKey: 'assistant.scriptDialog.wizard.triggerTitle',
    helperKey: 'assistant.scriptDialog.triggerSubtitle',
  },
  policy: {
    eyebrowKey: 'assistant.scriptDialog.wizard.policyEyebrow',
    titleKey: 'assistant.scriptDialog.wizard.policyTitle',
    helperKey: 'assistant.scriptDialog.playbackPolicySubtitle',
  },
  interrupt: {
    eyebrowKey: 'assistant.scriptDialog.wizard.interruptEyebrow',
    titleKey: 'assistant.scriptDialog.wizard.interruptTitle',
    helperKey: 'assistant.scriptDialog.whenInterruptedSubtitle',
  },
  dialogue: {
    eyebrowKey: 'assistant.scriptDialog.wizard.dialogueEyebrow',
    titleKey: 'assistant.scriptDialog.wizard.dialogueTitle',
    helperKey: 'assistant.scriptDialog.wizard.dialogueHelper',
  },
  languages: {
    eyebrowKey: 'assistant.scriptDialog.wizard.languagesEyebrow',
    titleKey: 'assistant.scriptDialog.wizard.languagesTitle',
    helperKey: 'assistant.scriptDialog.wizard.languagesHelper',
  },
};

// Mirrors the visibility rules TriggerSection used to encode via nested
// conditionals (policy/interrupt sections + the allow-skip toggle):
// - `policy` applies to area_entry (full list) and ui_click (single
//   "Always" option, shown for confirmation — see PlaybackPolicySection).
// - `interrupt` (InterruptSection + AllowSkipToggle) is entirely absent for
//   `object` triggers, and shows only the allow-skip toggle for ui_click /
//   custom_spot (InterruptSection itself stays hidden there since
//   button-driven scripts are always forced non_interruptible on save).
export const getVisibleQuestions = (triggerType: TriggerType): QuestionKey[] =>
  QUESTION_KEYS.filter((key) => {
    if (key === 'policy') {
      return triggerType === 'area_entry' || triggerType === 'ui_click';
    }

    if (key === 'interrupt') {
      return triggerType !== 'object';
    }

    return true;
  });
